import { useEffect, useId, useRef, useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWorkflows } from '@/features/workflows';
import { cn } from '@/lib/utils';

import type { TriggerActionFormValues, TriggerActionKind } from '../types';
import { SignalTemplateField } from './SignalTemplateField';
import { WorkflowSelect } from './WorkflowSelect';
import { WorkflowStepSelect } from './WorkflowStepSelect';

const ACTION_CHOICES: {
  value: TriggerActionKind;
  label: string;
  consequence: string;
}[] = [
  {
    value: 'create_task',
    label: 'Create a new task',
    consequence: 'Places a task on a chosen step of a target workflow.',
  },
  {
    value: 'continue_task',
    label: 'Continue the existing task',
    consequence: 'Adds a comment and moves the source task to a chosen step.',
  },
];

/** A row is valid once it is either untouched (dropped silently) or fully
 *  filled in — exactly one side filled is the only invalid shape, the same
 *  "required together" rule a template pair needs to mean anything. Module
 *  scope, not a closure: it captures nothing from the component, so keeping
 *  it here avoids ever needing it in an effect's dependency array. */
function isInputRowValid(row: { key: string; value: string }): boolean {
  return (row.key.trim() === '') === (row.value === '');
}

export interface TriggerActionFieldsProps {
  workspaceId: string;
  /** Which driver's variable manifest the template fields below autocomplete
   *  against (spec 074, US3) — a `TriggerProfile.type`. */
  driverType: string;
  action: TriggerActionFormValues;
  onActionChange: (next: TriggerActionFormValues) => void;
  /** Surfaces a server refusal attached to one action field, same lookup
   *  `CreateTriggerProfileSheet.actionFieldError` provided before extraction. */
  actionFieldError: (name: string) => string | undefined;
  disabled: boolean;
  /** Clears a server refusal once the person edits the field it complained
   *  about — same convention every other field in this feature follows. */
  onClearError: () => void;
  /**
   * The workflow whose steps scope a `continue_task` action's target-step
   * picker. Spec 1's existing form passes its watched workflow; a scheduled
   * GitHub/GitLab profile (Spec 070) has no watched workflow of its own, so
   * it passes the UI-only `TargetWorkflowSelect` value instead
   * (research.md D6) — either way, this component never submits it itself.
   */
  scopingWorkflowId: string | undefined;
  /**
   * Mirrors the host form's own RHF `formState.isSubmitted` — reveals a
   * half-filled input-value-template row's message only after a submit was
   * attempted, the same gating every other field in this feature already
   * gets from RHF directly. This component has no form of its own to read
   * that flag from, so the host passes it down.
   */
  isSubmitted: boolean;
  /**
   * Fires whenever the input-row editor's validity changes, so the host
   * form can gate its own submit on it without reaching into this
   * component's internal row state — the one piece of validation that
   * lives here rather than in the host's RHF schema, because a half-filled
   * row is UI state with no shape `triggerProfileFormSchema` (or its
   * scheduled sibling) can express.
   */
  onRowsValidityChange?: (hasInvalidRows: boolean) => void;
  /**
   * Which action kinds the active driver may submit (Spec 082 research.md
   * D2) — filters `ACTION_CHOICES`. The host computes this from its own
   * `driver` (`workspace_task_change` gets `['create_task']` only;
   * `github`/`gitlab` get both), not this component — which action kinds a
   * driver supports is a business rule that belongs with whichever caller
   * already knows the driver, not duplicated here. Does not itself reset an
   * already-selected value that becomes unavailable; the host is
   * responsible for that (FR-009).
   */
  availableActionKinds: TriggerActionKind[];
}

/**
 * The Create New Task / Continue Existing Task action-configuration
 * fields — extracted verbatim out of `CreateTriggerProfileSheet` (research.md
 * D1) so a second driver's form can reuse them without reimplementing them
 * (FR-009). Behavior, validation messages, and defaults are unchanged from
 * before the extraction; only the fields' host component changed.
 *
 * Fully controlled rather than wired through `Controller`/`register`:
 * `action` is a discriminated union, and RHF cannot type a cross-branch
 * dotted path (`action.titleTemplate` / `action.commentTemplate`) against a
 * union cleanly — the same reasoning the pre-extraction component's own
 * comment documented. The input-value-template row editor
 * (`inputRows`/`nextInputRowId`) is `create_task`-only local UI state with
 * no wire representation of its own, so it now lives here with the fields
 * it edits rather than in the host form — its validity is surfaced to the
 * host via `onRowsValidityChange` (a plain callback) rather than an
 * imperative ref handle, so nothing here reads a ref during render.
 */
export function TriggerActionFields({
  workspaceId,
  driverType,
  action,
  onActionChange,
  actionFieldError,
  disabled,
  onClearError,
  scopingWorkflowId,
  isSubmitted,
  onRowsValidityChange,
  availableActionKinds,
}: TriggerActionFieldsProps) {
  const { workflows } = useWorkflows(workspaceId);

  const scopingWorkflow = workflows.find((wf) => wf.id === scopingWorkflowId);
  const targetWorkflow =
    action.kind === 'create_task'
      ? workflows.find((wf) => wf.id === action.targetWorkflowId)
      : undefined;

  const actionGroupId = useId();
  const actionLabelId = useId();
  const titleTemplateId = useId();
  const descriptionTemplateId = useId();
  const commentTemplateId = useId();

  /**
   * The create-task action's optional `input_value_templates` (FR-002),
   * edited as a plain list of key/template rows rather than directly as the
   * `Record<string, string>` the wire shape needs — a record has no stable
   * per-entry identity to key React elements on or to hold a half-typed key
   * while its value is still blank. `nextInputRowId` gives each row a stable
   * identity across add/remove that array index alone cannot (removing a row
   * shifts every later index).
   */
  const nextInputRowId = useRef(0);
  const [inputRows, setInputRows] = useState<
    { id: number; key: string; value: string }[]
  >([]);

  useEffect(() => {
    onRowsValidityChange?.(inputRows.some((row) => !isInputRowValid(row)));
  }, [inputRows, onRowsValidityChange]);

  /** Rebuilds `action.inputValueTemplates` from the current rows, dropping
   *  untouched (fully empty) ones — this is the only place the row list is
   *  serialized into the wire-shape map the form actually submits. */
  function syncInputValueTemplates(
    rows: { id: number; key: string; value: string }[],
  ) {
    if (action.kind !== 'create_task') return;
    const inputValueTemplates: Record<string, string> = {};
    for (const row of rows) {
      if (row.key.trim() !== '' && row.value !== '') {
        inputValueTemplates[row.key.trim()] = row.value;
      }
    }
    onActionChange({ ...action, inputValueTemplates });
  }

  function addInputRow() {
    setInputRows([
      ...inputRows,
      { id: nextInputRowId.current++, key: '', value: '' },
    ]);
  }

  function updateInputRow(
    id: number,
    patch: Partial<{ key: string; value: string }>,
  ) {
    const next = inputRows.map((row) =>
      row.id === id ? { ...row, ...patch } : row,
    );
    setInputRows(next);
    syncInputValueTemplates(next);
  }

  function removeInputRow(id: number) {
    const next = inputRows.filter((row) => row.id !== id);
    setInputRows(next);
    syncInputValueTemplates(next);
  }

  function switchActionKind(kind: TriggerActionKind) {
    onActionChange(
      kind === 'create_task'
        ? {
            kind: 'create_task',
            targetWorkflowId: '',
            targetStepId: '',
            titleTemplate: '',
            descriptionTemplate: null,
            inputValueTemplates: {},
          }
        : {
            kind: 'continue_task',
            commentTemplate: '',
            sourceStepId: '',
            targetStepId: '',
          },
    );
    // The row editor is create-task-only UI state with no continue-task
    // equivalent — reset alongside the field it edits, same as every other
    // action-specific field switching kind resets.
    setInputRows([]);
    onClearError();
  }

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <span id={actionLabelId} className="text-sm leading-none font-medium">
          Action
        </span>
        <div
          role="radiogroup"
          aria-labelledby={actionLabelId}
          className="grid gap-2 sm:grid-cols-2"
        >
          {ACTION_CHOICES.filter((choice) =>
            availableActionKinds.includes(choice.value),
          ).map((choice) => (
            <label
              key={choice.value}
              className={cn(
                'border-border hover:bg-muted/50 flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                action.kind === choice.value && 'border-ring bg-muted/50',
              )}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name={actionGroupId}
                  value={choice.value}
                  checked={action.kind === choice.value}
                  disabled={disabled}
                  onChange={() => {
                    switchActionKind(choice.value);
                  }}
                  className="accent-primary"
                />
                <span className="text-sm font-medium">{choice.label}</span>
              </span>
              <span className="text-muted-foreground text-xs">
                {choice.consequence}
              </span>
            </label>
          ))}
        </div>
      </div>

      {action.kind === 'create_task' ? (
        <div className="flex flex-col gap-3 border-t pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Target workflow</Label>
              <WorkflowSelect
                workspaceId={workspaceId}
                value={action.targetWorkflowId}
                disabled={disabled}
                aria-label="Target workflow"
                // Deliberately no `filter` here (research.md D3): nothing
                // in the backend contract forbids a create-task action
                // targeting the same workflow the profile watches.
                onChange={(next) => {
                  onActionChange({
                    ...action,
                    targetWorkflowId: next,
                    targetStepId: '',
                  });
                  onClearError();
                }}
              />
              {actionFieldError('targetWorkflowId') ? (
                <p className="text-destructive text-xs">
                  {actionFieldError('targetWorkflowId')}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Target step</Label>
              <WorkflowStepSelect
                workflow={targetWorkflow}
                value={action.targetStepId}
                disabled={disabled}
                aria-label="Target step"
                onChange={(next) => {
                  onActionChange({ ...action, targetStepId: next });
                  onClearError();
                }}
              />
              {actionFieldError('targetStepId') ? (
                <p className="text-destructive text-xs">
                  {actionFieldError('targetStepId')}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={titleTemplateId}>Title template</Label>
            <SignalTemplateField
              id={titleTemplateId}
              workspaceId={workspaceId}
              driverType={driverType}
              value={action.titleTemplate}
              disabled={disabled}
              rows={1}
              placeholder="Implement: {{ signal.task.title }}"
              onChange={(next) => {
                onActionChange({
                  ...action,
                  titleTemplate: next,
                });
              }}
            />
            {actionFieldError('titleTemplate') ? (
              <p className="text-destructive text-xs">
                {actionFieldError('titleTemplate')}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={descriptionTemplateId}>
              Description template{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <SignalTemplateField
              id={descriptionTemplateId}
              workspaceId={workspaceId}
              driverType={driverType}
              value={action.descriptionTemplate ?? ''}
              disabled={disabled}
              rows={2}
              onChange={(next) => {
                onActionChange({
                  ...action,
                  descriptionTemplate: next.trim() === '' ? null : next,
                });
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>
                Input value templates{' '}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={addInputRow}
              >
                <PlusIcon aria-hidden="true" />
                Add mapping
              </Button>
            </div>
            {inputRows.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                None configured — the created task gets no input values beyond
                its target step&apos;s own defaults.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {inputRows.map((row, index) => {
                  const rowInvalid = isSubmitted && !isInputRowValid(row);
                  const rowMessage = !rowInvalid
                    ? undefined
                    : row.key.trim() === ''
                      ? 'Key is required.'
                      : 'Template value is required.';
                  return (
                    <div key={row.id} className="flex flex-col gap-1">
                      <div className="flex gap-2">
                        <Input
                          aria-label={`Input key ${String(index + 1)}`}
                          placeholder="input_key"
                          className="font-mono"
                          value={row.key}
                          disabled={disabled}
                          aria-invalid={rowInvalid ? true : undefined}
                          onChange={(event) => {
                            updateInputRow(row.id, {
                              key: event.target.value,
                            });
                          }}
                        />
                        <Input
                          aria-label={`Input template ${String(index + 1)}`}
                          placeholder="{{ signal.task.id }}"
                          className="font-mono"
                          value={row.value}
                          disabled={disabled}
                          aria-invalid={rowInvalid ? true : undefined}
                          onChange={(event) => {
                            updateInputRow(row.id, {
                              value: event.target.value,
                            });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={disabled}
                          aria-label={`Remove mapping ${String(index + 1)}`}
                          onClick={() => {
                            removeInputRow(row.id);
                          }}
                        >
                          <XIcon aria-hidden="true" />
                        </Button>
                      </div>
                      {rowMessage ? (
                        <p className="text-destructive text-xs">{rowMessage}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 border-t pt-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={commentTemplateId}>Comment template</Label>
            <SignalTemplateField
              id={commentTemplateId}
              workspaceId={workspaceId}
              driverType={driverType}
              value={action.commentTemplate}
              disabled={disabled}
              rows={3}
              placeholder="Approved by {{ signal.task.title }}"
              onChange={(next) => {
                onActionChange({
                  ...action,
                  commentTemplate: next,
                });
              }}
            />
            {actionFieldError('commentTemplate') ? (
              <p className="text-destructive text-xs">
                {actionFieldError('commentTemplate')}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Source step</Label>
            <WorkflowStepSelect
              // A continue-task action has no `target_workflow_id` field on
              // the wire (backend `ContinueTaskAction`) — its steps are
              // always within the *scoping* workflow (Spec 1: the watched
              // workflow; Spec 070: `TargetWorkflowSelect`'s UI-only value),
              // never a separately chosen one.
              workflow={scopingWorkflow}
              value={action.sourceStepId}
              disabled={disabled}
              // Spec 076 US4/FR-014: the matched task must currently be
              // sitting on a human-gate step for this action to apply.
              humanGateOnly
              aria-label="Source step"
              onChange={(next) => {
                onActionChange({ ...action, sourceStepId: next });
                onClearError();
              }}
            />
            {actionFieldError('sourceStepId') ? (
              <p className="text-destructive text-xs">
                {actionFieldError('sourceStepId')}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Target step</Label>
            <WorkflowStepSelect
              workflow={scopingWorkflow}
              value={action.targetStepId}
              disabled={disabled}
              aria-label="Target step"
              onChange={(next) => {
                onActionChange({ ...action, targetStepId: next });
                onClearError();
              }}
            />
            {actionFieldError('targetStepId') ? (
              <p className="text-destructive text-xs">
                {actionFieldError('targetStepId')}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
